import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Wrench, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ApprovalGate({ incident, onDecision }) {
  const approval = incident?.approval;
  const [comment, setComment] = useState("");
  const [modifications, setModifications] = useState("");
  const [busy, setBusy] = useState(false);

  if (approval) {
    const tone =
      approval.decision === "approve"
        ? "text-emerald-300 border-emerald-500/40"
        : approval.decision === "reject"
          ? "text-rose-300 border-rose-500/40"
          : "text-amber-300 border-amber-500/40";
    return (
      <div
        className={`cre-surface rounded-sm p-6 border ${tone}`}
        data-testid="approval-recorded"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5" strokeWidth={1.5} />
          <div>
            <div className="cre-overline">Approval gate</div>
            <div className="font-heading text-lg font-bold tracking-tight">
              Decision recorded: {approval.decision.toUpperCase()}
            </div>
          </div>
        </div>
        <div className="mt-3 text-sm text-zinc-300">
          <div>
            <span className="cre-overline mr-2">Reviewer</span>
            {approval.reviewer}
          </div>
          {approval.comment ? (
            <div className="mt-1">
              <span className="cre-overline mr-2">Comment</span>
              {approval.comment}
            </div>
          ) : null}
          {approval.modifications ? (
            <div className="mt-1">
              <span className="cre-overline mr-2">Modifications</span>
              <span className="font-mono text-xs">{approval.modifications}</span>
            </div>
          ) : null}
          <div className="mt-2 text-xs text-zinc-500 font-mono">
            {approval.decided_at}
          </div>
        </div>
      </div>
    );
  }

  const send = async (decision) => {
    if (decision === "modify" && !modifications.trim()) {
      toast.error("Please describe the modifications");
      return;
    }
    setBusy(true);
    try {
      await onDecision({
        decision,
        reviewer: "On-call Reviewer",
        comment: comment.trim(),
        modifications: decision === "modify" ? modifications.trim() : null,
      });
      toast.success(`Runbook ${decision}d`);
    } catch (e) {
      toast.error("Decision failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="cre-approval-beam rounded-sm p-[1px]"
      data-testid="approval-gate"
    >
      <div className="rounded-sm p-6 bg-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm border border-blue-500/40 bg-blue-500/10 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-blue-300" strokeWidth={1.5} />
            </div>
            <div>
              <div className="cre-overline text-blue-300">Human approval &amp; governance</div>
              <h3 className="font-heading text-xl font-bold tracking-tight">
                Review and authorize remediation
              </h3>
            </div>
          </div>
        </div>

        <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
          The CRE will not execute anything automatically. Approve to mark the runbook
          ready for a controlled execution (advisory only — human operators perform actions), reject to discard, or
          request modifications.
        </p>

        <div className="mt-4">
          <Textarea
            placeholder="Reviewer comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="bg-black/40 border-white/10 text-sm"
            data-testid="approval-comment"
          />
          <Textarea
            placeholder="Describe modifications (required when choosing 'Modify')"
            value={modifications}
            onChange={(e) => setModifications(e.target.value)}
            className="mt-2 bg-black/40 border-white/10 text-sm font-mono"
            data-testid="approval-modifications"
          />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
          <Button
            data-testid="approve-runbook-button"
            disabled={busy}
            onClick={() => send("approve")}
            className="rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
          >
            <Check className="h-4 w-4 mr-2" strokeWidth={2} /> Approve
          </Button>
          <Button
            data-testid="modify-runbook-button"
            disabled={busy}
            onClick={() => send("modify")}
            variant="outline"
            className="rounded-sm border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
          >
            <Wrench className="h-4 w-4 mr-2" strokeWidth={1.75} /> Modify
          </Button>
          <Button
            data-testid="reject-runbook-button"
            disabled={busy}
            onClick={() => send("reject")}
            variant="outline"
            className="rounded-sm border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
          >
            <X className="h-4 w-4 mr-2" strokeWidth={2} /> Reject
          </Button>
        </div>
      </div>
    </div>
  );
}
